// DSR 처리 큐 + 학습셋 제외 로그 — 05-admin §4.6.
// 소스: GET /v1/compliance/dsr (01 §3.K) → dsr_requests 상태머신
//   received → propagating → purging → tombstoned → done (실패: failed).
// 참고: 01 §3.K의 DSR 목록은 viewer 스코프(운영자 자신)다. 어드민 전용 목록 엔드포인트가
// 계약에 없으므로 graceful degradation으로 처리한다(준비중/empty).
import { api, errMsg, isNotReady } from "../core/api.js";
import { h, mount, notReadyState } from "../core/dom.js";
import type { DsrKind, DsrRequest, DsrState, Page } from "../models.js";

const KIND_LABEL: Record<DsrKind, string> = {
  training_optout: "학습 opt-out",
  delete_account: "계정 삭제",
  delete_content: "콘텐츠 삭제",
  object: "이의제기",
  export: "내보내기",
};

const STATE_LABEL: Record<DsrState, string> = {
  received: "접수",
  propagating: "학습셋 제외 전파",
  purging: "R2/Stream 삭제",
  tombstoned: "lineage 툼스톤",
  done: "완료",
  failed: "실패",
};

function stateBadge(s: DsrState): HTMLElement {
  const cls = s === "done" ? "ok" : s === "failed" ? "danger" : "warn";
  return h("span", { class: `badge ${cls}` }, STATE_LABEL[s] ?? s);
}

function fmtTime(ms: number): string {
  return new Date(ms).toLocaleString("ko-KR");
}

function elapsed(ms: number): string {
  const days = Math.floor((Date.now() - ms) / 86_400_000);
  return days <= 0 ? "오늘" : `${days}일 경과`;
}

// 학습셋 제외 로그(log_json) 뷰 — §4.6.
function logDialog(r: DsrRequest): void {
  const backdrop = h("div", { class: "dialog-backdrop" });
  function pretty(json?: string | null): string {
    if (!json) return "—";
    try {
      return JSON.stringify(JSON.parse(json), null, 2);
    } catch {
      return json;
    }
  }
  backdrop.appendChild(
    h(
      "div",
      { class: "dialog wide" },
      h("h3", {}, `DSR ${KIND_LABEL[r.kind] ?? r.kind}`),
      h("p", { class: "muted small" }, `상태: ${STATE_LABEL[r.state] ?? r.state} · ${fmtTime(r.created_at)}`),
      h("h4", {}, "처리 로그 (학습셋 제외 / R2·Stream 퍼지 / lineage 툼스톤)"),
      h("pre", {}, pretty(r.log_json)),
      h("div", { class: "row" }, h("button", { class: "btn secondary", onclick: () => backdrop.remove() }, "닫기")),
    ),
  );
  document.body.appendChild(backdrop);
}

export async function complianceView(_p: Record<string, string>, root: HTMLElement): Promise<void> {
  let activeKind: DsrKind | "all" = "all";

  const tabs = h("div", { class: "tabs" });
  const listWrap = h("div", { class: "card" });

  async function load(): Promise<void> {
    listWrap.replaceChildren(h("div", { class: "state muted" }, "DSR 큐 불러오는 중…"));
    try {
      const page = await api.get<Page<DsrRequest> | DsrRequest[]>("/v1/compliance/dsr?limit=50");
      const all = Array.isArray(page) ? page : (page.items ?? []);
      const items = activeKind === "all" ? all : all.filter((r) => r.kind === activeKind);
      if (items.length === 0) {
        listWrap.replaceChildren(h("div", { class: "state muted" }, "해당 조건의 DSR 요청이 없습니다."));
        return;
      }
      const table = h(
        "table",
        { class: "tbl" },
        h(
          "thead",
          {},
          h(
            "tr",
            {},
            h("th", {}, "종류"),
            h("th", {}, "상태"),
            h("th", {}, "접수"),
            h("th", {}, "경과(법정 기한)"),
            h("th", {}, ""),
          ),
        ),
        h(
          "tbody",
          {},
          ...items.map((r) =>
            h(
              "tr",
              {},
              h("td", {}, h("span", { class: "badge" }, KIND_LABEL[r.kind] ?? r.kind)),
              h("td", {}, stateBadge(r.state)),
              h("td", { class: "muted small" }, fmtTime(r.created_at)),
              h("td", { class: "muted small" }, r.done_at ? "완료" : elapsed(r.created_at)),
              h("td", {}, h("button", { class: "btn small", onclick: () => logDialog(r) }, "제외 로그")),
            ),
          ),
        ),
      );
      listWrap.replaceChildren(table);
    } catch (e) {
      if (isNotReady(e)) {
        listWrap.replaceChildren(notReadyState("DSR 큐 (/v1/compliance/dsr) 가 아직 응답하지 않습니다."));
        return;
      }
      listWrap.replaceChildren(h("div", { class: "state error" }, `DSR 큐 불러오기 실패: ${errMsg(e)}`));
    }
  }

  function renderTabs(): void {
    const kinds: (DsrKind | "all")[] = [
      "all",
      "training_optout",
      "delete_content",
      "delete_account",
      "object",
      "export",
    ];
    tabs.replaceChildren(
      ...kinds.map((k) =>
        h(
          "button",
          {
            class: `tab ${k === activeKind ? "active" : ""}`,
            onclick: () => {
              activeKind = k;
              renderTabs();
              void load();
            },
          },
          k === "all" ? "전체" : KIND_LABEL[k],
        ),
      ),
    );
  }

  renderTabs();
  mount(
    root,
    h(
      "div",
      { class: "stack" },
      h("h1", {}, "DSR 처리 큐 (데이터주체 권리)"),
      h(
        "p",
        { class: "muted" },
        "삭제·이의·opt-out 요청의 상태머신(received→propagating→purging→tombstoned→done)과 학습셋 제외 로그를 추적합니다 (05-admin §4.6). legal_hold가 걸린 객체는 purging 단계에서 건너뛰며 보류 표기됩니다.",
      ),
      tabs,
      listWrap,
    ),
  );
  await load();
}
